import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';

interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'user';
  organizations: string[];
  lastOrganizationId?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, firstName?: string, lastName?: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  resendVerification: (email: string) => Promise<{ error?: string }>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updateLastOrganization: (orgId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (supabaseUser: User) => {
    try {
      console.log('fetchUserProfile: Starting for user:', supabaseUser.id, supabaseUser.email);

      // Get user profile with timeout
      console.log('fetchUserProfile: About to query users table...');
      const { data: userProfile, error: userError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, role, last_organization_id')
        .eq('id', supabaseUser.id)
        .single();

      if (userError) {
        Sentry.captureException(userError);
        // If user doesn't exist in users table, create a basic user object
        if (userError.code === 'PGRST116') {
          console.log('fetchUserProfile: User not found in users table, creating basic auth user');
          const basicAuthUser = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            firstName: supabaseUser.user_metadata?.first_name,
            lastName: supabaseUser.user_metadata?.last_name,
            role: 'user' as const,
            organizations: [],
            lastOrganizationId: undefined
          };
          console.log('fetchUserProfile: Returning basic auth user:', basicAuthUser);
          return basicAuthUser;
        }
        return null;
      }

      // Fetch and cache organizations based on user role
      let userOrgs: { organization_id: string }[] = [];

      console.log('fetchUserProfile: Fetching organizations for user');
      const { data: allOrgs, error: allOrgsError } = await supabase
        .from('organizations')
        .select('id, name, logo_url, created_at, updated_at');

      console.log('fetchUserProfile: Org query result:', { allOrgs, allOrgsError });

      if (!allOrgsError && allOrgs) {
        userOrgs = allOrgs.map(org => ({ organization_id: org.id }));

        // Cache organizations in localStorage (no timestamp needed for background refresh)
        const orgCache = allOrgs.map(org => ({
          organization_id: org.id,
          organization_name: org.name,
          logo_url: org.logo_url,
          created_at: org.created_at,
          updated_at: org.updated_at
        }));
        console.log('fetchUserProfile: Caching organizations:', orgCache);
        localStorage.setItem('cachedOrganizations', JSON.stringify(orgCache));
      } else {
        Sentry.captureException(allOrgsError);
      }

      const authUserData = {
        id: userProfile.id,
        email: userProfile.email,
        firstName: userProfile.first_name,
        lastName: userProfile.last_name,
        role: userProfile.role,
        organizations: userOrgs?.map(org => org.organization_id) || [],
        lastOrganizationId: userProfile.last_organization_id
      };

      console.log('fetchUserProfile: Returning user data:', authUserData);
      return authUserData;
    } catch (error) {
      Sentry.captureException(error);

      // If there's a database error, create a basic user object to prevent infinite loading
      if (error instanceof Error && error.message.includes('timeout')) {
        console.log('fetchUserProfile: Query timed out, creating basic auth user');
        const basicAuthUser = {
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          firstName: supabaseUser.user_metadata?.first_name,
          lastName: supabaseUser.user_metadata?.last_name,
          role: 'user' as const,
          organizations: [],
          lastOrganizationId: undefined
        };
        return basicAuthUser;
      }

      return null;
    }
  };

  const updateLastOrganization = async (orgId: string) => {
    try {
      const { error } = await supabase.rpc('update_user_last_organization', {
        org_id: orgId
      });

      if (error) {
        Sentry.captureException(error);
        throw error;
      }

      // Update local user state
      if (user) {
        setUser({ ...user, lastOrganizationId: orgId });
      }

      // Refresh the session to get updated JWT with new organization claims
      console.log('Refreshing session to update JWT claims...');
      const { error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        Sentry.captureException(refreshError);
        // Don't throw here as the organization was updated successfully
      } else {
        console.log('Session refreshed successfully with new organization claims');
      }
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { error: error.message };
      }

      const userProfile = await fetchUserProfile(data.user);
      setUser(userProfile);

      // Set user context in Sentry for troubleshooting
      if (userProfile) {
        Sentry.setUser({
          id: userProfile.id,
          email: userProfile.email,
          username: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
        });
      }

      return {};
    } catch (error) {
      Sentry.captureException(error);
      return { error: 'An unexpected error occurred' };
    }
  };

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            first_name: firstName,
            last_name: lastName
          }
        }
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      Sentry.captureException(error);
      return { error: 'An unexpected error occurred' };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      Sentry.captureException(error,);
      return { error: 'An unexpected error occurred' };
    }
  };

  const resendVerification = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;

      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          email
        }
      });
      return { error: 'An unexpected error occurred' };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl
      });

      if (error) {
        return { error: error.message };
      }

      return {};
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          email
        }
      });
      return { error: 'An unexpected error occurred' };
    }
  };

  const signOut = async () => {
    // Clear all cached data on logout
    localStorage.removeItem('cachedOrganizations');
    localStorage.removeItem('selectedOrganization');

    await supabase.auth.signOut();
    setUser(null);

    // Clear user and organization context in Sentry
    Sentry.setUser(null);
    Sentry.setContext('organization', null);
  };

  useEffect(() => {
    // Check for existing session
    if (user) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.email);
      if (session?.user?.id != user?.id) {
        console.log('Initial session: Fetching user profile...');
        fetchUserProfile(session.user).then(userProfile => {
          console.log('Initial session: User profile result:', userProfile);
          setUser(userProfile);

          // Set user context in Sentry for troubleshooting
          if (userProfile) {
            Sentry.setUser({
              id: userProfile.id,
              email: userProfile.email,
              username: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
            });
          }

          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    // Add Supabase auth state change listener
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Don't fetch user profile if we're still loading
      if (loading) return;

      console.log('Auth state changed:', event);
      if (session?.user && session.user.id != user?.id) {
        setLoading(true);
        const userProfile = await fetchUserProfile(session.user);
        setUser(userProfile);

        // Set user context in Sentry for troubleshooting
        if (userProfile) {
          Sentry.setUser({
            id: userProfile.id,
            email: userProfile.email,
            username: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
          });
        }

        setLoading(false);
      } else {
        setUser(null);
        // Clear user context in Sentry
        Sentry.setUser(null);
        setLoading(false);
      }
    });

    // Cleanup listener on unmount
    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        signUp,
        signOut,
        signInWithGoogle,
        resendVerification,
        resetPassword,
        updateLastOrganization
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
